import { useContext, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { IdentityProviderClientContext } from "../../../shared/services/api/contexts";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import AuthPage, { EmailCheckNotification } from "../../../shared/components/ui/AuthPage";
import { MFAType } from "../../../models/auth";
import { assert, unknownType } from "../../../shared/utils/assert";
import { useQuery } from "../../../shared/utils/use_query";

export default function SignInPage() {
  const [loading, setLoading] = useState<boolean>(false);
  const identityProviderClient = useContext(IdentityProviderClientContext);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const query = useQuery();

  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const [hidePSCButton, setHidePSCButton] = useState<boolean>(false);

  useEffect(() => {
    // the notification is inside this useEffect to avoid too many renders limit to prevent infinite loop
    const pscIdToken = localStorage.getItem("psc-id-token");
    if (pscIdToken) {
      showSnackbar({
        text: "You are already logged in with Pro Santé Connect. Please log out to use another account.",
        color: "danger",
      });
      setHidePSCButton(true);
    }
  }, [setHidePSCButton, showSnackbar]);

  const [mfa, setMfa] = useState<MFAType | undefined>(undefined);

  const onSubmit = async (email: string, password: string) => {
    // Clear previous errors
    setErrors({});

    // Basic validation
    const newErrors: { email?: string; password?: string } = {};
    if (!email) {
      newErrors.email = "Email is required";
    }
    if (!password) {
      newErrors.password = "Password is required"; // NOSONAR - This is an error message, not a credential
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const mfaChallenge = await identityProviderClient.signIn(email, password);
      switch (mfaChallenge) {
        case MFAType.SMS_MFA:
          setMfa(MFAType.SMS_MFA);
          break;
        case MFAType.SOFTWARE_TOKEN_MFA:
          setMfa(MFAType.SOFTWARE_TOKEN_MFA);
          break;
        case null:
          navigate("/");
          break;
        default:
          unknownType(mfaChallenge);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      if (error.code === "UserNotFoundException") {
        setErrors({ email: "User not found." });
        return;
      }
      if (error.code === "NotAuthorizedException") {
        showSnackbar({
          text: "Incorrect username or password.",
          color: "danger",
        });
        return;
      }
      console.error(error);
      showSnackbar({
        text: "Could not sign in",
        color: "danger",
      });
      return;
    }
  };

  const [emailCheckNotification, setEmailCheckNotification] =
    useState<EmailCheckNotification | null>(null);

  useEffect(() => {
    const status = query.get("email_status");
    if (status === "verified") {
      setEmailCheckNotification({
        error: false,
        message: "Thank you for verifying your email! You can now log in.",
      });
    } else if (status === "error") {
      setEmailCheckNotification({
        error: true,
        message: "Invalid or expired verification link. Contact support if you need assistance.",
      });
    }
  }, [query]);

  if (identityProviderClient.isAuthenticated()) {
    return <Navigate to="/" />;
  }

  assert(window.CONFIG.auth.kind === "AWS_COGNITO", "expected AWS_COGNITO auth");
  const pscConfig = window.CONFIG.auth.pscConfig;

  return (
    <AuthPage
      loading={loading}
      onSubmit={onSubmit}
      errors={errors}
      cloud
      hidePSCButton={hidePSCButton}
      pscConfig={pscConfig}
      mfaType={mfa}
      email_check_notification={emailCheckNotification}
    />
  );
}
